<template>
  <button type="button" class="histRow" :class="{ expanded }" @click="$emit('toggle')">
    <span class="chev" :class="{ open: expanded }"><AppIcon name="chevron_right" :size="14" /></span>

    <span class="contactCell">
      <span class="cNameRow">
        <span class="cName" :title="item.name || item.prospect_e164">{{ item.name || item.prospect_e164 }}</span>
        <CrmLinkChips class="cCrm" :crm-ids="item.crm_ids" compact />
        <AgentPill v-if="item.agent_only" class="cAgent" />
      </span>
      <!-- The main row carries no phone number any more — numbers belong to the touches
           in the expanded timeline. A multi-line person gets a muted count instead. -->
      <span v-if="item.title || lineCount > 1" class="cSub">
        <span v-if="item.title" class="cTitle" :title="item.title">{{ item.title }}</span>
        <span v-if="item.title && lineCount > 1" class="dot">·</span>
        <span v-if="lineCount > 1">{{ lineCount }} lines</span>
      </span>
    </span>

    <span class="acctCell">
      <Avatar :name="accountLabel" size="sm" />
      <span class="acctName" :title="accountLabel || undefined">{{ accountLabel || '—' }}</span>
    </span>

    <span v-if="hasPlays ?? true" class="playCell">
      <span v-if="item.sales_play_name" class="pdot" :style="{ background: playDotColor }"></span>
      <span class="pname" :title="item.sales_play_name || undefined">{{ item.sales_play_name || '—' }}</span>
    </span>

    <span class="attemptsCell">{{ item.attempts }}</span>

    <span class="dispCell">
      <DispositionPill :code="item.last_disposition_code" />
    </span>

    <span class="byCell" :title="item.last_attempt_by_name || undefined">{{ item.last_attempt_by_name || '——' }}</span>

    <span class="lastCell">{{ formatAttemptStamp(item.last_attempt_at) }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import Avatar from '../../../components/dialer/Avatar.vue'
import DispositionPill from './DispositionPill.vue'
import CrmLinkChips from './CrmLinkChips.vue'
import AgentPill from './settings/AgentPill.vue'
import { formatAttemptStamp } from '../../utils/localTime'
import type { DialerHistoryItem } from '../../../services/dialerApi'

// hasPlays=false (dialer-only tenants) drops the play cell; the page narrows the grid to match.
const props = defineProps<{ item: DialerHistoryItem; expanded: boolean; hasPlays?: boolean }>()
defineEmits<{ (e: 'toggle'): void }>()

// Accounts are domain-keyed and `name` is often null (enriched later); fall back to the
// domain so the cell isn't an empty.
const accountLabel = computed(() => props.item.account_name || props.item.account_domain || '')

const lineCount = computed(() => props.item.numbers?.length ?? 0)

// Per-play color dot (visual grouping cue, matching the mockup). Hashed from the play
// name so the same play is always the same hue without a server-provided color.
const playDotColor = computed(() => {
  const s = props.item.sales_play_name || ''
  if (!s) return 'transparent'
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360
  return `hsl(${h}, 55%, 55%)`
})
</script>

<style scoped>
.histRow {
  display: grid;
  grid-template-columns: var(--hist-grid);
  gap: 12px;
  align-items: center;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  background: transparent;
  border: 0;
  border-top: 1px solid var(--color-border, #e5e2dc);
  padding: 10px 12px;
  cursor: pointer;
  transition: background 100ms ease;
}
.histRow:hover,
.histRow.expanded {
  background: var(--color-bg-subtle, #f2ede5);
}

.chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary, #a3a3a3);
  transition: transform 120ms ease;
}
.chev.open {
  transform: rotate(90deg);
}

.contactCell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.cNameRow {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.cCrm {
  flex: none;
}
.cAgent {
  flex: none;
}
.cName {
  font-weight: 700;
  color: var(--color-text-primary, var(--text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.cSub {
  display: flex;
  gap: 5px;
  min-width: 0;
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
}
.cTitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cSub .dot {
  opacity: 0.6;
}

.acctCell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.acctName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-secondary, #525252);
}

.playCell {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  font-size: 13px;
  color: var(--color-text-secondary, #525252);
}
.pdot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}
.pname {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attemptsCell {
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary, #525252);
}

.dispCell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.dispCell .dash {
  color: var(--color-text-tertiary, #a3a3a3);
}
.recDot {
  display: inline-flex;
  color: var(--color-text-tertiary, #a3a3a3);
  flex: none;
}

.lastCell {
  font-size: 13px;
  color: var(--color-text-secondary, #525252);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.byCell {
  font-size: 13px;
  color: var(--color-text-secondary, #525252);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
</style>
